import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, Box, Fab, FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput, TextField } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import * as yup from 'yup';
import { ValidationError } from 'yup';
import { type Criteria, type Criterion } from '../../shared/types';
import { CRITERIAS } from '../../shared/utilities';

type Props = {
    onAdd: (value: Criterion) => void;
    label?: string;
    disableList?: Criterion[];
};

export const CriterionEntry = ({ onAdd, label = 'Criteria', disableList = [] }: Props) => {
    const [error, setError] = useState<string | null>(null);
    const [selectedThreshold, setSelectedThreshold] = useState<string>('');
    const [selectedCriteria, setSelectedCriteria] = useState<Criteria | null>(null);
    const disableFilter = disableList.map(({ criteria: { text } }) => text);

    const handleCriteriaChange = (_: React.SyntheticEvent, value: Criteria | null) => {
        setSelectedCriteria(value);
        setError(null);

        if (!value) {
            setSelectedThreshold('');
        }
    };

    const getHelperText = () => {
        if (!selectedCriteria) return '';

        if (error && error.match(/^this must be a `number`/)) {
            return 'Value must be a number';
        }

        if (error) {
            return error.replace(/^this/, 'Value');
        }

        return `Value between ${selectedCriteria.min} and ${selectedCriteria.max} ${selectedCriteria.unit}`;
    };

    const handleThresholdChange = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const threshold = event.target.value as string;

        setSelectedThreshold(threshold);
    };

    const handleThresholdBlur = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        try {
            if (!selectedCriteria) return;

            setError(null);

            const threshold = event.target.value as string;
            const thresholdSchema = yup.number().min(selectedCriteria.min).max(selectedCriteria.max).required();

            await thresholdSchema.validate(threshold);
        } catch (error: ValidationError | any) {
            if (Array.isArray(error.errors)) {
                setError(error.errors[0]);
            }
        }
    };

    const handleAdd = async () => {
        try {
            if (!selectedCriteria) return;
            const thresholdSchema = yup.number().min(selectedCriteria.min).max(selectedCriteria.max).required();

            const threshold = await thresholdSchema.validate(selectedThreshold);

            onAdd({ threshold, criteria: selectedCriteria });

            setError(null);
            setSelectedCriteria(null);
            setSelectedThreshold('');
        } catch (error: ValidationError | any) {
            if (Array.isArray(error.errors)) {
                setError(error.errors[0]);
            }
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl fullWidth={true}>
                    <Autocomplete
                        fullWidth={true}
                        options={CRITERIAS}
                        getOptionLabel={({ text }) => text}
                        getOptionDisabled={({ text }) => disableFilter.includes(text)}
                        renderInput={(params) => {
                            return <TextField {...params} variant="outlined" label={label} />;
                        }}
                        onChange={handleCriteriaChange}
                        value={selectedCriteria}
                    />
                </FormControl>
                <FormControl variant="outlined" sx={{ minWidth: 120, width: 120 }} error={Boolean(error)}>
                    <InputLabel variant="outlined" htmlFor="value">
                        Value
                    </InputLabel>
                    <OutlinedInput
                        id="threshold"
                        label="Value"
                        endAdornment={
                            <InputAdornment position="end" sx={{ fontWeight: 600 }}>
                                {selectedCriteria ? selectedCriteria.unit : ''}
                            </InputAdornment>
                        }
                        onChange={handleThresholdChange}
                        onBlur={handleThresholdBlur}
                        value={selectedThreshold}
                    />
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0.5 }}>
                    <Fab color="primary" size="small" onClick={handleAdd}>
                        <AddIcon />
                    </Fab>
                </Box>
            </Box>
            <FormHelperText error={Boolean(error)}>{getHelperText()}</FormHelperText>
        </Box>
    );
};
